import { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

// ── Constants ──────────────────────────────────────────────────────────────
const SA_TOKEN_KEY  = "sa_token";
// Vocabulaire réel des sociétés (companies.status + archived) — tenants
// (solo/tpe/pme/groupe, actif/suspendu/résilié/impayé) n'a plus cours,
// cette session a reconnecté tout superadmin.js sur companies.
const COMPANY_STATUS_COLORS = { trial: "#f59e0b", readonly: "#f97316", active: "#16a34a", archived: "#6b7280" };
const COMPANY_STATUS_LABELS = { trial: "Essai", readonly: "Lecture seule", active: "Active", archived: "Archivée" };
const PRIORITY_COLORS = { basse: "#6b7280", normale: "#3b82f6", haute: "#f59e0b", critique: "#ef4444" };
const TABS = [
  { id: "dashboard",      label: "Tableau de bord",    icon: "📊" },
  { id: "tenants",        label: "Abonnés",             icon: "🏢" },
  { id: "demo",           label: "Démo",                icon: "🧪" },
  { id: "subscriptions",  label: "Abonnements",         icon: "💳" },
  { id: "support",        label: "Support",             icon: "🎫" },
  { id: "modules",        label: "Modules",             icon: "🧩" },
  { id: "monitoring",     label: "Monitoring",          icon: "🖥️" },
  { id: "communications", label: "Communications",      icon: "📢" },
  { id: "logs",           label: "Audit",               icon: "🔒" },
];

function fmt(n)   { return (n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 }); }
function fmtE(n)  { return fmt(n) + " €"; }
function fmtPct(n){ return (n || 0).toFixed(1) + "%"; }

// ── Login page ──────────────────────────────────────────────────────────────
function LoginPage({ onLogin }) {
  const [email, setEmail]     = useState("");
  const [password, setPassword] = useState("");
  const [totp, setTotp]       = useState("");
  const [needs2FA, setNeeds2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const r = await api.superadmin.login({ email, password, totp_code: totp || undefined });
    setLoading(false);
    if (r.requires_2fa)  { setNeeds2FA(true); return; }
    if (r.error)         { setError(r.error); return; }
    if (r.token)         { localStorage.setItem(SA_TOKEN_KEY, r.token); onLogin(r.token); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0d1117", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 16, padding: 40, width: 380 }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>⚡</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#f1f5f9" }}>Superadmin</div>
          <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>FinanceApp — Accès restreint</div>
        </div>
        {error && <div style={{ background: "#fef2f2", color: "#dc2626", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16, border: "1px solid #fecaca" }}>{error}</div>}
        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {!needs2FA ? (
            <>
              <div>
                <label style={{ display: "block", fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>Email superadmin</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
                  style={{ width: "100%", padding: "10px 12px", background: "#0d1117", border: "1px solid #30363d", borderRadius: 8, color: "#f1f5f9", fontSize: 14, boxSizing: "border-box" }}
                  placeholder="vous@example.com" />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>Mot de passe</label>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                  style={{ width: "100%", padding: "10px 12px", background: "#0d1117", border: "1px solid #30363d", borderRadius: 8, color: "#f1f5f9", fontSize: 14, boxSizing: "border-box" }} />
              </div>
            </>
          ) : (
            <div>
              <div style={{ fontSize: 13, color: "#94a3b8", marginBottom: 12 }}>Entrez votre code Google Authenticator</div>
              <input type="text" value={totp} onChange={e => setTotp(e.target.value)} maxLength={6} required
                style={{ width: "100%", padding: "14px 12px", background: "#0d1117", border: "1px solid #30363d", borderRadius: 8, color: "#f1f5f9", fontSize: 24, textAlign: "center", letterSpacing: 8, boxSizing: "border-box" }}
                placeholder="000000" autoFocus />
            </div>
          )}
          <button type="submit" disabled={loading} style={{ padding: "11px 0", background: "#1a9e72", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", marginTop: 4 }}>
            {loading ? "Connexion..." : needs2FA ? "Valider le code" : "Se connecter"}
          </button>
          {needs2FA && <button type="button" onClick={() => setNeeds2FA(false)} style={{ fontSize: 12, color: "#64748b", background: "none", border: "none", cursor: "pointer" }}>← Retour</button>}
        </form>
        <div style={{ marginTop: 24, padding: "12px 14px", background: "#0d111780", borderRadius: 8, fontSize: 11, color: "#475569" }}>
          🔒 Accès sécurisé · JWT 4h · IP tracking · Audit complet
        </div>
      </div>
    </div>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────
function KCard({ label, value, sub, icon, color = "#1a9e72", small }) {
  return (
    <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: small ? "14px 16px" : "18px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
        <span style={{ fontSize: 20 }}>{icon}</span>
      </div>
      <div style={{ fontSize: small ? 20 : 24, fontWeight: 800, color: color || "#f1f5f9" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#475569", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// ── Dashboard Tab ──────────────────────────────────────────────────────────
function DashboardTab({ tok }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.superadmin.dashboard(tok).then(r => { if (!r.error) setData(r); setLoading(false); });
  }, [tok]);

  if (loading) return <div style={{ color: "#64748b", padding: 60, textAlign: "center" }}>Chargement...</div>;
  if (!data) return null;
  const { kpis, statusBreakdown = [], tenantEvol, mrrEvol, billing_enabled } = data;

  const statusChartData = statusBreakdown
    .filter(s => s.cnt > 0)
    .map(s => ({ name: COMPANY_STATUS_LABELS[s.status] || s.status, value: s.cnt, color: COMPANY_STATUS_COLORS[s.status] || "#6b7280" }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Gel plateforme (PLATFORM_BILLING_ENABLED) — état global, pas par société */}
      {billing_enabled === false && (
        <div style={{ background: "#f59e0b18", border: "1px solid #f59e0b55", borderRadius: 10, padding: "10px 16px", color: "#f59e0b", fontSize: 12, fontWeight: 600 }}>
          ⚠️ Facturation plateforme gelée (PLATFORM_BILLING_ENABLED=false) — aucune société ne peut être bloquée pour essai expiré tant que ce gel est actif.
        </div>
      )}
      {/* KPI grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
        <KCard label="Total sociétés" value={fmt(kpis.totalCompanies)} icon="🏢" color="#3b82f6" />
        <KCard label="MRR" value={fmtE(kpis.mrr)} sub={`ARR : ${fmtE(kpis.arr)}`} icon="💰" color="#1a9e72" />
        <KCard label="Actives" value={fmt(kpis.actif)} sub={`${fmt(kpis.trial)} en essai`} icon="✅" color="#16a34a" />
        <KCard label="Nouveaux / mois" value={`+${fmt(kpis.newThisMonth)}`} icon="🚀" color="#8b5cf6" />
        <KCard label="Conversion essai→payant" value={fmtPct(kpis.conversionRate)} sub={`${fmt(kpis.paidCount)}/${fmt(kpis.everTrialed)} sociétés`} icon="📈" color="#1a9e72" />
        <KCard label="Tickets ouverts" value={fmt(kpis.ticketsOpen)} sub={`${fmt(kpis.ticketsInProgress)} en cours`} icon="🎫" color="#f59e0b" />
        <KCard label="Lecture seule" value={fmt(kpis.readonly)} sub="essai expiré, en grâce" icon="⏸️" color="#f97316" />
        <KCard label="Archivées" value={fmt(kpis.archived)} icon="🗑️" color="#6b7280" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 300px", gap: 20 }}>
        {/* Tenant evolution */}
        <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#f1f5f9", marginBottom: 14 }}>Évolution abonnés (12 mois)</div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={tenantEvol}>
              <defs>
                <linearGradient id="tg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1a9e72" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#1a9e72" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} />
              <Tooltip contentStyle={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 8 }} />
              <Area type="monotone" dataKey="total" stroke="#1a9e72" fill="url(#tg)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* MRR evolution */}
        <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#f1f5f9", marginBottom: 14 }}>MRR mensuel</div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={mrrEvol}>
              <CartesianGrid strokeDasharray="3 3" stroke="#21262d" />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 8 }} formatter={v => [fmtE(v), "MRR"]} />
              <Bar dataKey="mrr" fill="#3b82f6" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Répartition par statut réel (trial/lecture seule/active/archivée) */}
        <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#f1f5f9", marginBottom: 14 }}>Répartition par statut</div>
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie data={statusChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} label={({ name, value }) => `${name}: ${value}`} labelLine={false} fontSize={10}>
                {statusChartData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 8 }} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
            {statusChartData.map(p => (
              <span key={p.name} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8, background: `${p.color}22`, color: p.color, fontWeight: 600 }}>
                {p.name} ({p.value})
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Tenants Tab ────────────────────────────────────────────────────────────
// Reconnecté à `companies` (la table `tenants` était vide, jamais
// alimentée). Pas de « plan » dans le modèle réel (tarification modulaire,
// cf. tenant_modules/tenant_billing_config) — remplacé par le montant
// mensuel réellement calculé. Création manuelle désactivée côté backend
// (une société se crée via l'inscription, jamais manuellement).
function TenantsTab({ tok }) {
  const [tenants, setTenants]   = useState([]);
  const [filter, setFilter]     = useState({ status: "", search: "" });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]     = useState(null);
  const [showEdit, setShowEdit]   = useState(null);
  const [msg, setMsg]           = useState("");
  const [form, setForm] = useState({ company_name: "", admin_email: "", admin_name: "", trial_end_date: "" });

  const load = useCallback(() => {
    api.superadmin.tenants(tok, filter).then(r => { if (Array.isArray(r?.tenants)) setTenants(r.tenants); });
  }, [tok, filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    api.superadmin.tenant(tok, selected.id).then(r => { if (!r.error) setDetail(r); });
  }, [selected, tok]);

  const action = async (fn, successMsg) => {
    const r = await fn();
    if (r.error) { setMsg("Erreur : " + r.error); setTimeout(() => setMsg(""), 4000); return; }
    setMsg(r.message || successMsg || "✓");
    setTimeout(() => setMsg(""), 3000);
    load();
    if (selected) { const r2 = await api.superadmin.tenant(tok, selected.id); if (!r2.error) setDetail(r2); }
  };

  const impersonate = async (t) => {
    const reason = prompt(`Raison de l'impersonation de "${t.company_name}" (min. 20 caractères, tracée et auditée) :`);
    if (!reason || reason.trim().length < 20) { if (reason !== null) setMsg("Raison trop courte (min. 20 caractères) — annulé"); return; }
    const r = await api.superadmin.impersonate(tok, t.id, { reason });
    if (r.error) return setMsg("Erreur : " + r.error);
    localStorage.setItem('token', r.token);
    window.open('/', '_blank');
    setMsg(`✓ Impersonation activée pour ${t.company_name} (nouvel onglet)`);
  };

  const suspend = (t) => {
    const reason = prompt(`Raison de la suspension de "${t.company_name}" :`);
    if (!reason || reason.trim().length < 5) { if (reason !== null) setMsg("Raison trop courte — annulé"); return; }
    action(() => api.superadmin.suspendTenant(tok, t.id, { reason }));
  };

  const save = async () => {
    const r = await api.superadmin.updateTenant(tok, showEdit.id, form);
    if (r.error) return setMsg("Erreur : " + r.error);
    setMsg("✓ Société mise à jour"); load(); setShowEdit(null);
  };

  const openEdit = (t) => {
    setForm({ company_name: t.company_name || "", admin_email: t.admin_email || "", admin_name: t.admin_name || "", trial_end_date: t.trial_end_date || "" });
    setShowEdit(t);
  };

  const inputSt = { width: "100%", padding: "8px 11px", background: "#0d1117", border: "1px solid #30363d", borderRadius: 8, color: "#f1f5f9", fontSize: 13, boxSizing: "border-box" };

  return (
    <div style={{ display: "flex", gap: 20 }}>
      {/* List */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Filters */}
        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          <input placeholder="Recherche société..." value={filter.search} onChange={e => setFilter(f => ({ ...f, search: e.target.value }))}
            style={{ ...inputSt, width: 220 }} />
          <select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))} style={{ ...inputSt, width: 160 }}>
            <option value="">Tous statuts</option>
            {["trial","readonly","active","archived"].map(s => <option key={s} value={s}>{COMPANY_STATUS_LABELS[s]}</option>)}
          </select>
          <div style={{ flex: 1 }} />
          {msg && <span style={{ fontSize: 12, color: msg.startsWith("✓") ? "#1a9e72" : "#ef4444", alignSelf: "center" }}>{msg}</span>}
        </div>
        <div style={{ fontSize: 11, color: "#475569", marginBottom: 10 }}>
          Les sociétés sont créées via l'inscription utilisateur — pas de création manuelle ici.
        </div>

        <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#0d1117" }}>
                {["Société","Email","Statut","Depuis","Montant/m","Modules","Activité","","Actions"].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: "#64748b", fontWeight: 600, fontSize: 11, borderBottom: "1px solid #21262d", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tenants.map(t => (
                <tr key={t.id} onClick={() => setSelected(s => s?.id === t.id ? null : t)}
                  style={{ borderBottom: "1px solid #21262d", cursor: "pointer", background: selected?.id === t.id ? "#1f2937" : "transparent", opacity: t.is_active === 0 ? 0.55 : 1 }}>
                  <td style={{ padding: "9px 12px", color: "#f1f5f9", fontWeight: 600 }}>{t.company_name}{t.is_active === 0 && <span style={{ marginLeft: 6, fontSize: 10, color: "#ef4444" }}>(suspendue)</span>}</td>
                  <td style={{ padding: "9px 12px", color: "#94a3b8" }}>{t.admin_email}</td>
                  <td style={{ padding: "9px 12px" }}>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8, background: `${COMPANY_STATUS_COLORS[t.status]}22`, color: COMPANY_STATUS_COLORS[t.status], fontWeight: 600 }}>{COMPANY_STATUS_LABELS[t.status] || t.status}</span>
                  </td>
                  <td style={{ padding: "9px 12px", color: "#64748b", fontSize: 11 }}>{t.created_at?.slice(0, 10)}</td>
                  <td style={{ padding: "9px 12px", color: "#1a9e72", fontWeight: 600 }}>{t.monthly_amount ? fmtE(t.monthly_amount) : "–"}</td>
                  <td style={{ padding: "9px 12px", color: "#94a3b8", fontSize: 11 }} title={t.active_modules?.join(', ') || ''}>{t.active_modules?.length || 0}</td>
                  <td style={{ padding: "9px 12px", color: "#64748b", fontSize: 11 }}>{t.last_activity ? t.last_activity.slice(0, 10) : "–"}</td>
                  <td style={{ padding: "9px 12px" }} title={
                    t.trial_blocked ? "Essai expiré, sans abonnement actif — écriture bloquée pour ce client"
                    : t.renewal_hard_blocked ? "Renouvellement non confirmé — période de grâce expirée, à relancer manuellement"
                    : t.renewal_blocked ? "Renouvellement non confirmé — en lecture seule"
                    : ""
                  }>
                    {t.trial_blocked && <span style={{ fontSize: 13 }}>⚠️</span>}
                    {!t.trial_blocked && t.renewal_hard_blocked && <span style={{ fontSize: 13 }}>🔴</span>}
                    {!t.trial_blocked && !t.renewal_hard_blocked && t.renewal_blocked && <span style={{ fontSize: 13 }}>⚠️</span>}
                  </td>
                  <td style={{ padding: "9px 12px" }} onClick={e => e.stopPropagation()}>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button title="Modifier" onClick={() => openEdit(t)} style={{ padding: "3px 7px", border: "1px solid #30363d", borderRadius: 5, background: "transparent", color: "#94a3b8", cursor: "pointer", fontSize: 11 }}>✏️</button>
                      <button title="Impersonate" onClick={() => impersonate(t)} style={{ padding: "3px 7px", border: "1px solid #30363d", borderRadius: 5, background: "transparent", color: "#f59e0b", cursor: "pointer", fontSize: 11 }}>🔑</button>
                      {t.is_active !== 0 && <button title="Suspendre" onClick={() => suspend(t)} style={{ padding: "3px 7px", border: "1px solid #30363d", borderRadius: 5, background: "transparent", color: "#6b7280", cursor: "pointer", fontSize: 11 }}>⏸️</button>}
                      {t.is_active === 0 && <button title="Réactiver" onClick={() => action(() => api.superadmin.reactivateTenant(tok, t.id))} style={{ padding: "3px 7px", border: "1px solid #30363d", borderRadius: 5, background: "transparent", color: "#1a9e72", cursor: "pointer", fontSize: 11 }}>▶️</button>}
                      <button title="Supprimer" onClick={() => { if (window.confirm(`Supprimer "${t.company_name}" ?`)) action(() => api.superadmin.deleteTenant(tok, t.id)); }} style={{ padding: "3px 7px", border: "1px solid #7f1d1d33", borderRadius: 5, background: "transparent", color: "#ef4444", cursor: "pointer", fontSize: 11 }}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
              {tenants.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: "center", padding: 40, color: "#475569" }}>Aucun abonné</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail panel */}
      {detail && (
        <div style={{ width: 340, flexShrink: 0, background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#f1f5f9", marginBottom: 4 }}>{detail.company_name}</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>{detail.admin_email}</div>
          </div>
          {[
            ["Statut", <span style={{ color: COMPANY_STATUS_COLORS[detail.status] }}>{COMPANY_STATUS_LABELS[detail.status] || detail.status}</span>],
            ["Accès", detail.is_active === 0 ? <span style={{ color: "#ef4444" }}>Suspendu</span> : <span style={{ color: "#1a9e72" }}>Actif</span>],
            ["Montant/m", fmtE(detail.monthly_amount)],
            ["Essai jusqu'au", detail.trial_end_date || "–"],
            ["Fin abonnement", detail.subscription_end || "–"],
            ["Dernière activité", detail.last_activity ? detail.last_activity.slice(0, 16).replace('T', ' ') : "Jamais connecté"],
            ["Blocage essai", detail.trial_blocked
              ? <span style={{ color: "#ef4444" }}>⚠️ Bloqué (essai expiré, sans abonnement)</span>
              : <span style={{ color: "#1a9e72" }}>Aucun</span>],
            ["Renouvellement", detail.renewal_hard_blocked
              ? <span style={{ color: "#ef4444" }}>🔴 Grâce expirée — à relancer</span>
              : detail.renewal_blocked
              ? <span style={{ color: "#f59e0b" }}>⚠️ En lecture seule</span>
              : <span style={{ color: "#1a9e72" }}>Aucun</span>],
            ["Quota IA (mois)", `${detail.ai_usage_month?.calls || 0} appel(s) · ${(detail.ai_usage_month?.cost_eur || 0).toFixed(4)} €`],
          ].map(([label, val]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, borderBottom: "1px solid #21262d", paddingBottom: 6 }}>
              <span style={{ color: "#64748b" }}>{label}</span>
              <span style={{ color: "#f1f5f9", fontWeight: 600 }}>{val}</span>
            </div>
          ))}

          {/* Modules actifs (payants, self-service côté client) — distinct
              des "Flags actifs" ci-dessous (kill-switches internes) */}
          <div>
            <div style={{ fontSize: 11, color: "#475569", textTransform: "uppercase", marginBottom: 6 }}>Modules actifs</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {detail.active_modules?.length > 0
                ? detail.active_modules.map(m => (
                    <span key={m} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 8, background: "#185fa522", color: "#60a5fa" }}>{m}</span>
                  ))
                : <span style={{ fontSize: 11, color: "#475569" }}>Aucun module payant actif</span>}
            </div>
          </div>

          {/* Quick actions */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ fontSize: 11, color: "#475569", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 2 }}>Actions rapides</div>
            <button onClick={() => { const d = prompt("Prolonger l'essai de X jours :","14"); if (d) action(() => api.superadmin.extendTrial(tok, detail.id, { days: parseInt(d) })); }}
              style={{ padding: "6px 12px", background: "#f59e0b22", color: "#f59e0b", border: "1px solid #f59e0b44", borderRadius: 7, fontSize: 12, cursor: "pointer", textAlign: "left" }}>
              ⏰ Prolonger l'essai gratuit
            </button>
            <button onClick={() => action(() => api.superadmin.giftMonth(tok, detail.id))}
              style={{ padding: "6px 12px", background: "#1a9e7222", color: "#1a9e72", border: "1px solid #1a9e7244", borderRadius: 7, fontSize: 12, cursor: "pointer", textAlign: "left" }}>
              🎁 Offrir 1 mois (abonnement actif requis)
            </button>
            <button onClick={() => { const pw = prompt("Nouveau mot de passe admin (min. 12 car.) :"); if (pw) action(() => api.superadmin.resetPassword(tok, detail.id, { new_password: pw })); }}
              style={{ padding: "6px 12px", background: "#8b5cf622", color: "#8b5cf6", border: "1px solid #8b5cf644", borderRadius: 7, fontSize: 12, cursor: "pointer", textAlign: "left" }}>
              🔑 Réinitialiser mot de passe
            </button>
            <button onClick={() => impersonate(detail)}
              style={{ padding: "6px 12px", background: "#0d1117", color: "#f59e0b", border: "1px solid #f59e0b88", borderRadius: 7, fontSize: 12, cursor: "pointer", textAlign: "left", fontWeight: 600 }}>
              👤 Connexion en tant que ce client
            </button>
          </div>

          {/* Feature flags */}
          {detail.flags?.length > 0 && (
            <div>
              <div style={{ fontSize: 11, color: "#475569", textTransform: "uppercase", marginBottom: 6 }}>Flags actifs</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {detail.flags.filter(f => f.enabled).map(f => (
                  <span key={f.flag_name} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 8, background: "#1a9e7222", color: "#1a9e72" }}>{f.flag_name}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit modal — pas de création manuelle (cf. note ci-dessus) */}
      {showEdit && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}>
          <div style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 14, padding: 28, width: 460 }}>
            <h3 style={{ margin: "0 0 18px", color: "#f1f5f9", fontSize: 16 }}>Modifier la société</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {[["Société *","company_name","text"],["Email admin","admin_email","email"],["Nom admin","admin_name","text"]].map(([label, key, type]) => (
                <div key={key}>
                  <label style={{ display: "block", fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>{label}</label>
                  <input type={type} style={inputSt} value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
                </div>
              ))}
              <div>
                <label style={{ display: "block", fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>Fin d'essai</label>
                <input type="date" style={inputSt} value={form.trial_end_date} onChange={e => setForm(f => ({ ...f, trial_end_date: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
              <button onClick={() => setShowEdit(null)} style={{ padding: "8px 16px", border: "1px solid #30363d", borderRadius: 8, background: "transparent", color: "#94a3b8", cursor: "pointer", fontSize: 13 }}>Annuler</button>
              <button onClick={save} style={{ padding: "8px 16px", background: "#1a9e72", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                Mettre à jour
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Support Tab ────────────────────────────────────────────────────────────
function SupportTab({ tok }) {
  const [tickets, setTickets]   = useState([]);
  const [filter, setFilter]     = useState({ status: "", priority: "" });
  const [selected, setSelected] = useState(null);
  const [detail, setDetail]     = useState(null);
  const [reply, setReply]       = useState("");
  const [msg, setMsg]           = useState("");

  const load = useCallback(() => {
    api.superadmin.tickets(tok, filter).then(r => { if (Array.isArray(r)) setTickets(r); });
  }, [tok, filter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    api.superadmin.ticket(tok, selected.id).then(r => { if (!r.error) setDetail(r); });
  }, [selected, tok]);

  const updateStatus = async (status) => {
    await api.superadmin.updateTicket(tok, selected.id, { ...detail, status });
    setMsg("✓ Statut mis à jour"); load();
    const r = await api.superadmin.ticket(tok, selected.id);
    if (!r.error) setDetail(r);
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    await api.superadmin.replyTicket(tok, selected.id, { message: reply });
    setReply("");
    const r = await api.superadmin.ticket(tok, selected.id);
    if (!r.error) setDetail(r);
    load();
  };

  const inputSt = { padding: "7px 10px", background: "#0d1117", border: "1px solid #30363d", borderRadius: 8, color: "#f1f5f9", fontSize: 12, width: "100%", boxSizing: "border-box" };

  return (
    <div style={{ display: "flex", gap: 16 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))} style={{ ...inputSt, width: 140 }}>
            <option value="">Tous statuts</option>
            {["ouvert","en_cours","en_attente","résolu","fermé"].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filter.priority} onChange={e => setFilter(f => ({ ...f, priority: e.target.value }))} style={{ ...inputSt, width: 120 }}>
            <option value="">Toutes priorités</option>
            {["basse","normale","haute","critique"].map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {msg && <span style={{ fontSize: 12, color: "#1a9e72", alignSelf: "center" }}>{msg}</span>}
        </div>

        <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#0d1117" }}>
                {["#","Abonné","Sujet","Priorité","Statut","Créé le"].map(h => (
                  <th key={h} style={{ padding: "9px 12px", textAlign: "left", color: "#64748b", fontWeight: 600, fontSize: 11, borderBottom: "1px solid #21262d" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tickets.map(t => (
                <tr key={t.id} onClick={() => setSelected(s => s?.id === t.id ? null : t)}
                  style={{ borderBottom: "1px solid #21262d", cursor: "pointer", background: selected?.id === t.id ? "#1f2937" : "transparent" }}>
                  <td style={{ padding: "8px 12px", color: "#64748b" }}>#{t.id}</td>
                  <td style={{ padding: "8px 12px", color: "#94a3b8" }}>{t.company_name || "–"}</td>
                  <td style={{ padding: "8px 12px", color: "#f1f5f9", fontWeight: t.priority === 'critique' ? 700 : 400 }}>{t.subject}</td>
                  <td style={{ padding: "8px 12px" }}>
                    <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 8, background: `${PRIORITY_COLORS[t.priority]}22`, color: PRIORITY_COLORS[t.priority], fontWeight: 600 }}>{t.priority}</span>
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 8, background: "#21262d", color: "#94a3b8" }}>{t.status}</span>
                  </td>
                  <td style={{ padding: "8px 12px", color: "#475569", fontSize: 11 }}>{t.created_at?.slice(0, 10)}</td>
                </tr>
              ))}
              {tickets.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", padding: 40, color: "#475569" }}>Aucun ticket</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ticket detail */}
      {detail && (
        <div style={{ width: 380, flexShrink: 0, background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>#{detail.id} {detail.subject}</div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{detail.company_name} · {detail.created_at?.slice(0, 16)}</div>
            </div>
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8, background: `${PRIORITY_COLORS[detail.priority]}22`, color: PRIORITY_COLORS[detail.priority], fontWeight: 600, flexShrink: 0 }}>{detail.priority}</span>
          </div>
          {detail.description && <div style={{ fontSize: 12, color: "#94a3b8", background: "#0d1117", padding: 10, borderRadius: 8, lineHeight: 1.6 }}>{detail.description}</div>}

          {/* Status change */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {["en_cours","en_attente","résolu","fermé"].map(s => (
              <button key={s} onClick={() => updateStatus(s)} style={{ padding: "4px 10px", background: detail.status === s ? "#1a9e72" : "transparent", color: detail.status === s ? "#fff" : "#94a3b8", border: "1px solid #30363d", borderRadius: 6, fontSize: 11, cursor: "pointer" }}>{s}</button>
            ))}
          </div>

          {/* Messages */}
          <div style={{ flex: 1, maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 8 }}>
            {detail.messages?.map(m => (
              <div key={m.id} style={{ padding: "8px 12px", borderRadius: 8, background: m.sender === 'superadmin' ? "#1a9e7222" : "#21262d", alignSelf: m.sender === 'superadmin' ? "flex-end" : "flex-start", maxWidth: "90%" }}>
                <div style={{ fontSize: 10, color: "#64748b", marginBottom: 4 }}>{m.sender === 'superadmin' ? 'Superadmin' : 'Client'} · {m.created_at?.slice(11, 16)}</div>
                <div style={{ fontSize: 12, color: "#f1f5f9", lineHeight: 1.5 }}>{m.message}</div>
              </div>
            ))}
          </div>

          {/* Reply */}
          <div style={{ display: "flex", gap: 8 }}>
            <textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Votre réponse..." rows={3}
              style={{ ...inputSt, resize: "none", flex: 1 }} />
            <button onClick={sendReply} style={{ padding: "6px 12px", background: "#1a9e72", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 12, alignSelf: "flex-end" }}>
              Envoyer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Démo Tab ───────────────────────────────────────────────────────────────
// Génération de liens d'accès à la société démo bac à sable (server/utils/
// demoData.js) — un seul lien actif à la fois, révoque et réinitialise
// automatiquement côté serveur à chaque nouvelle génération.
function DemoTab({ tok }) {
  const [links, setLinks] = useState([]);
  const [form, setForm] = useState({ prospect_name: "", prospect_email: "", days: 7 });
  const [generated, setGenerated] = useState(null);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => { api.superadmin.demoLinks(tok).then(r => { if (Array.isArray(r?.links)) setLinks(r.links); }); };
  useEffect(() => { load(); }, [tok]); // eslint-disable-line react-hooks/exhaustive-deps

  const inputSt = { width: "100%", padding: "8px 11px", background: "#0d1117", border: "1px solid #30363d", borderRadius: 8, color: "#f1f5f9", fontSize: 13, boxSizing: "border-box" };

  const generate = async () => {
    if (!form.prospect_name && !form.prospect_email) { setMsg("Nom ou email du prospect requis."); return; }
    setLoading(true);
    const r = await api.superadmin.createDemoLink(tok, form);
    setLoading(false);
    if (r.error) { setMsg("Erreur : " + r.error); return; }
    setGenerated(r);
    setMsg("");
    load();
  };

  const revoke = async (id) => {
    if (!window.confirm("Révoquer ce lien ? Le prospect ne pourra plus y accéder.")) return;
    await api.superadmin.revokeDemoLink(tok, id);
    load();
  };

  const statusColor = { actif: "#1a9e72", "expiré": "#6b7280", "révoqué": "#ef4444" };

  return (
    <div style={{ display: "flex", gap: 20 }}>
      <div style={{ width: 340, flexShrink: 0, background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9", marginBottom: 4 }}>Nouveau lien de démo</div>
        <div style={{ fontSize: 11, color: "#64748b", marginBottom: 14 }}>
          Génère un lien vers la société démo bac à sable, pré-remplie de données réalistes. Un seul lien actif à la fois — en créer un nouveau révoque le précédent et réinitialise les données.
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <label style={{ display: "block", fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>Nom du prospect</label>
            <input style={inputSt} value={form.prospect_name} onChange={e => setForm(f => ({ ...f, prospect_name: e.target.value }))} placeholder="Jean Dupont" />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>Email du prospect</label>
            <input style={inputSt} value={form.prospect_email} onChange={e => setForm(f => ({ ...f, prospect_email: e.target.value }))} placeholder="jean@exemple.fr" />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, color: "#94a3b8", marginBottom: 4 }}>Durée de validité (jours)</label>
            <input type="number" min={1} max={30} style={inputSt} value={form.days} onChange={e => setForm(f => ({ ...f, days: parseInt(e.target.value) || 7 }))} />
          </div>
          <button onClick={generate} disabled={loading}
            style={{ padding: "9px 14px", background: "#1a9e72", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
            {loading ? "Génération…" : "🔗 Générer le lien"}
          </button>
          {msg && <div style={{ fontSize: 12, color: "#ef4444" }}>{msg}</div>}
          {generated && (
            <div style={{ marginTop: 6, padding: 12, background: "#0d2a1e", border: "1px solid rgba(26,158,114,0.3)", borderRadius: 8 }}>
              <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 6 }}>Lien généré (copiez-le maintenant — il ne sera plus affiché) :</div>
              <input readOnly value={generated.url} onFocus={e => e.target.select()} style={{ ...inputSt, fontSize: 11 }} />
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>Expire le {new Date(generated.expires_at).toLocaleString('fr-FR')}</div>
            </div>
          )}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9", marginBottom: 10 }}>Historique des liens</div>
        <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#0d1117" }}>
                {["Prospect", "Statut", "Expire le", "Accès", "Dernier accès", ""].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: "#64748b", fontWeight: 600, fontSize: 11, borderBottom: "1px solid #21262d", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {links.map(l => (
                <tr key={l.id} style={{ borderBottom: "1px solid #21262d" }}>
                  <td style={{ padding: "9px 12px", color: "#f1f5f9" }}>{l.prospect_name || "–"}{l.prospect_email && <div style={{ fontSize: 10, color: "#64748b" }}>{l.prospect_email}</div>}</td>
                  <td style={{ padding: "9px 12px" }}>
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8, background: `${statusColor[l.status]}22`, color: statusColor[l.status], fontWeight: 600 }}>{l.status}</span>
                  </td>
                  <td style={{ padding: "9px 12px", color: "#64748b", fontSize: 11 }}>{new Date(l.expires_at).toLocaleDateString('fr-FR')}</td>
                  <td style={{ padding: "9px 12px", color: "#94a3b8", fontSize: 11 }}>{l.access_count || 0}</td>
                  <td style={{ padding: "9px 12px", color: "#64748b", fontSize: 11 }}>{l.last_accessed_at ? l.last_accessed_at.slice(0, 16).replace('T', ' ') : "Jamais"}</td>
                  <td style={{ padding: "9px 12px" }}>
                    {l.status === "actif" && (
                      <button onClick={() => revoke(l.id)} style={{ padding: "3px 8px", border: "1px solid #7f1d1d33", borderRadius: 5, background: "transparent", color: "#ef4444", cursor: "pointer", fontSize: 11 }}>Révoquer</button>
                    )}
                  </td>
                </tr>
              ))}
              {links.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: "center", padding: 40, color: "#475569" }}>Aucun lien généré</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ── Modules Tab ────────────────────────────────────────────────────────────
function ModulesTab({ tok, tenants }) {
  const [tenantId, setTenantId] = useState("");
  const [flags, setFlags]       = useState([]);
  const [msg, setMsg]           = useState("");

  useEffect(() => {
    if (!tenantId) { setFlags([]); return; }
    api.superadmin.flags(tok, tenantId).then(r => { if (r.flags) setFlags(r.flags); });
  }, [tenantId, tok]);

  const toggle = async (flag) => {
    const cur = flag.enabled === null ? false : flag.enabled;
    const r = await api.superadmin.setFlag(tok, { tenant_id: parseInt(tenantId), flag_name: flag.name, enabled: !cur });
    setMsg(r.message || "✓");
    setTimeout(() => setMsg(""), 3000);
    api.superadmin.flags(tok, tenantId).then(r => { if (r.flags) setFlags(r.flags); });
  };

  const inputSt = { padding: "8px 11px", background: "#0d1117", border: "1px solid #30363d", borderRadius: 8, color: "#f1f5f9", fontSize: 13 };

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center" }}>
        <select value={tenantId} onChange={e => setTenantId(e.target.value)} style={{ ...inputSt, minWidth: 280 }}>
          <option value="">Sélectionnez un abonné...</option>
          {tenants.map(t => <option key={t.id} value={t.id}>{t.company_name} ({COMPANY_STATUS_LABELS[t.status] || t.status})</option>)}
        </select>
        {msg && <span style={{ fontSize: 12, color: "#1a9e72" }}>{msg}</span>}
      </div>

      {tenantId && flags.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {flags.map(flag => {
            const active = flag.enabled === true;
            const override = flag.enabled !== null;
            return (
              <div key={flag.name} style={{ background: "#161b22", border: `1px solid ${active ? "#1a9e7244" : "#21262d"}`, borderRadius: 10, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <div style={{ fontSize: 13, color: "#f1f5f9", fontWeight: 600 }}>{flag.name}</div>
                  <div style={{ fontSize: 11, color: override ? (active ? "#1a9e72" : "#ef4444") : "#475569", marginTop: 2 }}>
                    {override ? (active ? "Activé (override)" : "Désactivé (override)") : "Selon plan"}
                  </div>
                </div>
                <button onClick={() => toggle(flag)} style={{
                  width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer",
                  background: active ? "#1a9e72" : "#30363d", position: "relative", transition: "background .15s",
                }}>
                  <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: active ? 22 : 3, transition: "left .15s" }} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {!tenantId && (
        <div style={{ textAlign: "center", padding: 80, color: "#475569" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🧩</div>
          <div style={{ fontSize: 14 }}>Sélectionnez un abonné pour gérer ses modules</div>
        </div>
      )}
    </div>
  );
}

// ── Monitoring Tab ──────────────────────────────────────────────────────────
function MonitoringTab({ tok }) {
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.superadmin.monitoring(tok).then(r => { if (!r.error) setData(r); setLoading(false); });
  };

  useEffect(() => { load(); }, [tok]);

  if (loading) return <div style={{ color: "#64748b", padding: 60, textAlign: "center" }}>Chargement...</div>;
  if (!data) return null;

  const StatusDot = ({ ok }) => <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: ok ? "#1a9e72" : "#ef4444", marginRight: 6 }} />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button onClick={load} style={{ padding: "7px 14px", background: "#161b22", border: "1px solid #30363d", color: "#94a3b8", borderRadius: 8, fontSize: 12, cursor: "pointer" }}>
          🔄 Actualiser
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
        {/* Server */}
        <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#f1f5f9", marginBottom: 14 }}>🖥️ Serveur</div>
          {[
            ["Statut", <><StatusDot ok={data.server.status === 'ok'} />{data.server.status}</>],
            ["Uptime", data.server.uptime_human],
            ["Node.js", data.server.node_version],
            ["RAM utilisée", `${data.server.memory_mb} MB`],
            ["RAM allouée", `${data.server.memory_total_mb} MB`],
          ].map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #21262d", fontSize: 12 }}>
              <span style={{ color: "#64748b" }}>{l}</span>
              <span style={{ color: "#f1f5f9" }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Database */}
        <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#f1f5f9", marginBottom: 14 }}>🗄️ Base de données</div>
          {[
            ["Statut", <><StatusDot ok={data.database.status === 'ok'} />{data.database.status}</>],
            ["Taille", `${data.database.size_mb} MB`],
            ["Abonnés", data.database.tenants],
            ["Plateforme OS", data.os.platform],
            ["CPUs", data.os.cpus],
            ["RAM libre OS", `${data.os.free_mem_mb} MB`],
          ].map(([l, v]) => (
            <div key={l} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid #21262d", fontSize: 12 }}>
              <span style={{ color: "#64748b" }}>{l}</span>
              <span style={{ color: "#f1f5f9" }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Cron jobs */}
        <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#f1f5f9", marginBottom: 14 }}>⏰ Cron jobs</div>
          {data.cronJobs.map(c => (
            <div key={c.name} style={{ padding: "8px 0", borderBottom: "1px solid #21262d" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 12, color: "#f1f5f9" }}>{c.name}</span>
                <StatusDot ok={c.status === 'scheduled'} />
              </div>
              <div style={{ fontSize: 10, color: "#475569", marginTop: 2 }}>{c.schedule}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent errors */}
      <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, padding: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "#f1f5f9", marginBottom: 14 }}>
          Erreurs récentes (24h)
          {data.recentErrors.length > 0 && <span style={{ marginLeft: 8, fontSize: 11, background: "#ef444422", color: "#ef4444", padding: "1px 7px", borderRadius: 8 }}>{data.recentErrors.length}</span>}
        </div>
        {data.recentErrors.length === 0 ? (
          <div style={{ fontSize: 13, color: "#1a9e72" }}>✓ Aucune erreur dans les dernières 24h</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {data.recentErrors.map(e => (
              <div key={e.id} style={{ padding: "8px 12px", background: "#0d1117", borderRadius: 8, borderLeft: "3px solid #ef4444" }}>
                <div style={{ fontSize: 12, color: "#f87171", fontWeight: 600 }}>{e.action}</div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{e.created_at?.slice(0, 16)} · IP: {e.ip_address}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Communications Tab ──────────────────────────────────────────────────────
function CommunicationsTab({ tok }) {
  const [comms, setComms]   = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm]     = useState({ title: "", message: "", type: "info", target_plans: "all", channel: "both", scheduled_at: "" });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg]       = useState("");

  const load = () => api.superadmin.communications(tok).then(r => { if (Array.isArray(r)) setComms(r); });
  useEffect(() => { load(); }, [tok]);

  const send = async () => {
    if (!form.title || !form.message) return;
    setSaving(true);
    const r = await api.superadmin.sendComm(tok, form);
    setSaving(false);
    setMsg(r.message || "✓ Envoyé");
    setShowCreate(false);
    load();
    setTimeout(() => setMsg(""), 4000);
  };

  const TYPE_COLORS = { info: "#3b82f6", maintenance: "#f59e0b", feature: "#1a9e72", alerte: "#ef4444" };
  const inputSt = { width: "100%", padding: "8px 11px", background: "#0d1117", border: "1px solid #30363d", borderRadius: 8, color: "#f1f5f9", fontSize: 13, boxSizing: "border-box" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: "#64748b" }}>{comms.length} communication(s)</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {msg && <span style={{ fontSize: 12, color: "#1a9e72" }}>{msg}</span>}
          <button onClick={() => setShowCreate(true)} style={{ padding: "7px 14px", background: "#1a9e72", color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
            📢 Nouvelle communication
          </button>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {comms.map(c => (
          <div key={c.id} style={{ background: "#161b22", border: `1px solid ${TYPE_COLORS[c.type]}33`, borderRadius: 12, padding: "14px 18px", display: "flex", gap: 16, alignItems: "flex-start" }}>
            <span style={{ fontSize: 24, flexShrink: 0 }}>
              {{ info: "ℹ️", maintenance: "🔧", feature: "🚀", alerte: "🚨" }[c.type]}
            </span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#f1f5f9" }}>{c.title}</div>
              <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4, lineHeight: 1.5 }}>{c.message}</div>
              <div style={{ fontSize: 11, color: "#475569", marginTop: 8 }}>
                Cible : {c.target_plans === 'all' ? 'toutes' : (COMPANY_STATUS_LABELS[c.target_plans] || c.target_plans)} · Canal : {c.channel} · {c.sent_at ? `Envoyé le ${c.sent_at?.slice(0, 10)} (${c.sent_count} destinataires)` : `Planifié : ${c.scheduled_at?.slice(0, 16)}`}
              </div>
            </div>
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 8, background: `${TYPE_COLORS[c.type]}22`, color: TYPE_COLORS[c.type], fontWeight: 600, flexShrink: 0 }}>{c.type}</span>
            <button onClick={async () => { await api.superadmin.deleteComm(tok, c.id); load(); }} style={{ background: "none", border: "none", cursor: "pointer", color: "#475569", fontSize: 16, flexShrink: 0 }}>✕</button>
          </div>
        ))}
        {comms.length === 0 && <div style={{ textAlign: "center", padding: 60, color: "#475569" }}>Aucune communication envoyée</div>}
      </div>

      {showCreate && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000 }}>
          <div style={{ background: "#161b22", border: "1px solid #30363d", borderRadius: 14, padding: 28, width: 520 }}>
            <h3 style={{ margin: "0 0 18px", color: "#f1f5f9", fontSize: 16 }}>Nouvelle communication</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <label style={{ display: "block", fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>Titre *</label>
                <input style={inputSt} value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>Message *</label>
                <textarea rows={4} style={{ ...inputSt, resize: "vertical" }} value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>Type</label>
                  <select style={inputSt} value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                    {["info","maintenance","feature","alerte"].map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>Cible</label>
                  <select style={inputSt} value={form.target_plans} onChange={e => setForm(f => ({ ...f, target_plans: e.target.value }))}>
                    <option value="all">Toutes les sociétés</option>
                    <option value="trial">En essai uniquement</option>
                    <option value="readonly">Lecture seule (essai expiré)</option>
                    <option value="active">Abonnées actives uniquement</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>Canal</label>
                  <select style={inputSt} value={form.channel} onChange={e => setForm(f => ({ ...f, channel: e.target.value }))}>
                    <option value="both">Email + In-app</option>
                    <option value="email">Email uniquement</option>
                    <option value="inapp">In-app uniquement</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ display: "block", fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>Envoi programmé (laisser vide pour envoi immédiat)</label>
                <input type="datetime-local" style={inputSt} value={form.scheduled_at} onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
              <button onClick={() => setShowCreate(false)} style={{ padding: "8px 16px", border: "1px solid #30363d", borderRadius: 8, background: "transparent", color: "#94a3b8", cursor: "pointer", fontSize: 13 }}>Annuler</button>
              <button onClick={send} disabled={saving} style={{ padding: "8px 16px", background: "#1a9e72", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>
                {saving ? "Envoi..." : form.scheduled_at ? "Planifier" : "Envoyer maintenant"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Subscriptions Tab ───────────────────────────────────────────────────────
function SubscriptionsTab({ tok }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.superadmin.subscriptions(tok).then(r => { if (!r.error) setData(r); }); }, [tok]);
  if (!data) return <div style={{ color: "#64748b", padding: 60, textAlign: "center" }}>Chargement...</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
        <KCard label="MRR mensuel" value={fmtE(data.revenue.month)} icon="💰" color="#1a9e72" />
        <KCard label="ARR annuel (proj.)" value={fmtE(data.revenue.year)} icon="📈" color="#3b82f6" />
        <KCard label="Taux conv. essai→payant" value={fmtPct(data.trialConversion)} icon="🎯" color="#8b5cf6" />
      </div>

      <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#0d1117" }}>
              {["Société","Email","Statut","Montant/m","Stripe ID","Fin abonnement"].map(h => (
                <th key={h} style={{ padding: "9px 12px", textAlign: "left", color: "#64748b", fontWeight: 600, fontSize: 11, borderBottom: "1px solid #21262d", whiteSpace: "nowrap" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map(t => (
              <tr key={t.id} style={{ borderBottom: "1px solid #21262d" }}>
                <td style={{ padding: "8px 12px", color: "#f1f5f9", fontWeight: 600 }}>{t.company_name}</td>
                <td style={{ padding: "8px 12px", color: "#94a3b8" }}>{t.admin_email}</td>
                <td style={{ padding: "8px 12px" }}><span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 8, background: `${COMPANY_STATUS_COLORS[t.status]}22`, color: COMPANY_STATUS_COLORS[t.status], fontWeight: 600 }}>{COMPANY_STATUS_LABELS[t.status] || t.status}</span></td>
                <td style={{ padding: "8px 12px", color: "#1a9e72", fontWeight: 700 }}>{fmtE(t.monthly_amount)}</td>
                <td style={{ padding: "8px 12px", color: "#475569", fontSize: 10 }}>{t.stripe_customer_id || "–"}</td>
                <td style={{ padding: "8px 12px", color: "#64748b", fontSize: 11 }}>{t.subscription_end?.slice(0, 10) || "–"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Audit Logs Tab ──────────────────────────────────────────────────────────
function LogsTab({ tok }) {
  const [logs, setLogs]   = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.superadmin.logs(tok, { limit: 200, action: filter }).then(r => { if (Array.isArray(r)) setLogs(r); });
  }, [tok, filter]);

  const inputSt = { padding: "7px 10px", background: "#0d1117", border: "1px solid #30363d", borderRadius: 8, color: "#f1f5f9", fontSize: 12 };

  return (
    <div>
      <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
        <input placeholder="Filtrer par action..." value={filter} onChange={e => setFilter(e.target.value)} style={{ ...inputSt, width: 260 }} />
      </div>
      <div style={{ background: "#161b22", border: "1px solid #21262d", borderRadius: 12, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr style={{ background: "#0d1117" }}>
              {["Date","Action","Cible","Détails","IP","Résultat"].map(h => (
                <th key={h} style={{ padding: "9px 12px", textAlign: "left", color: "#64748b", fontWeight: 600, borderBottom: "1px solid #21262d" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {logs.map(l => (
              <tr key={l.id} style={{ borderBottom: "1px solid #21262d" }}>
                <td style={{ padding: "7px 12px", color: "#475569", whiteSpace: "nowrap" }}>{l.created_at?.slice(0, 16)}</td>
                <td style={{ padding: "7px 12px", color: "#94a3b8", fontWeight: 600 }}>{l.action}</td>
                <td style={{ padding: "7px 12px", color: "#64748b" }}>{l.target_id || "–"}</td>
                <td style={{ padding: "7px 12px", color: "#475569", maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.details}</td>
                <td style={{ padding: "7px 12px", color: "#475569" }}>{l.ip_address}</td>
                <td style={{ padding: "7px 12px" }}>
                  <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 6, background: l.success ? "#1a9e7222" : "#ef444422", color: l.success ? "#1a9e72" : "#ef4444", fontWeight: 700 }}>
                    {l.success ? "✓ OK" : "✗ ECHEC"}
                  </span>
                </td>
              </tr>
            ))}
            {logs.length === 0 && <tr><td colSpan={6} style={{ textAlign: "center", padding: 40, color: "#475569" }}>Aucun log</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Main Superadmin Component ──────────────────────────────────────────────
export default function Superadmin() {
  const [token, setToken]   = useState(() => localStorage.getItem(SA_TOKEN_KEY) || "");
  const [tab, setTab]       = useState("dashboard");
  const [tenants, setTenants] = useState([]);

  // Validate stored token
  useEffect(() => {
    if (!token) return;
    api.superadmin.dashboard(token).then(r => {
      if (r.error) { localStorage.removeItem(SA_TOKEN_KEY); setToken(""); }
    });
  }, []);

  useEffect(() => {
    if (!token) return;
    api.superadmin.tenants(token).then(r => { if (Array.isArray(r?.tenants)) setTenants(r.tenants); });
  }, [token]);

  const logout = async () => {
    await api.superadmin.logout(token);
    localStorage.removeItem(SA_TOKEN_KEY);
    setToken("");
  };

  if (!token) return <LoginPage onLogin={setToken} />;

  // Check for impersonation banner — le JWT d'impersonation pose
  // is_impersonation (pas "impersonating") et company_name (pas
  // "tenant_name"), cf. server/routes/superadmin.js POST /tenants/:id/impersonate.
  const isImpersonating = (() => {
    try {
      const p = JSON.parse(atob((localStorage.getItem("token") || "..").split(".")[1] || "e30="));
      return p.is_impersonation ? p : null;
    } catch { return null; }
  })();

  return (
    <div style={{ minHeight: "100vh", background: "#0d1117", color: "#e2e8f0" }}>
      {/* Impersonation banner */}
      {isImpersonating && (
        <div style={{ background: "#dc2626", color: "#fff", padding: "8px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 13, fontWeight: 600 }}>
          <span>⚠️ MODE SUPERADMIN — Vous consultez le compte de <strong>{isImpersonating.company_name}</strong></span>
          <button onClick={() => { localStorage.removeItem("token"); window.close(); }} style={{ padding: "4px 12px", background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)", borderRadius: 6, color: "#fff", cursor: "pointer", fontSize: 12 }}>
            Quitter l'impersonation
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{ background: "#161b22", borderBottom: "1px solid #21262d", padding: "0 24px", display: "flex", alignItems: "center", gap: 16, height: 56 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 22 }}>⚡</span>
          <span style={{ fontSize: 16, fontWeight: 800, color: "#f1f5f9" }}>Superadmin</span>
          <span style={{ fontSize: 10, background: "#ef444422", color: "#ef4444", padding: "2px 7px", borderRadius: 8, fontWeight: 700 }}>RESTRICTED</span>
        </div>
        <div style={{ flex: 1, display: "flex", gap: 2 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: "6px 12px", background: "transparent", border: "none", cursor: "pointer",
              color: tab === t.id ? "#1a9e72" : "#64748b", fontSize: 12, fontWeight: tab === t.id ? 700 : 400,
              borderBottom: `2px solid ${tab === t.id ? "#1a9e72" : "transparent"}`,
            }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
        <button onClick={logout} style={{ padding: "6px 12px", background: "transparent", border: "1px solid #30363d", borderRadius: 8, color: "#64748b", cursor: "pointer", fontSize: 12 }}>
          Déconnexion
        </button>
      </div>

      {/* Content */}
      <div style={{ padding: 24 }}>
        {tab === "dashboard"      && <DashboardTab tok={token} />}
        {tab === "tenants"        && <TenantsTab tok={token} />}
        {tab === "demo"           && <DemoTab tok={token} />}
        {tab === "subscriptions"  && <SubscriptionsTab tok={token} />}
        {tab === "support"        && <SupportTab tok={token} />}
        {tab === "modules"        && <ModulesTab tok={token} tenants={tenants} />}
        {tab === "monitoring"     && <MonitoringTab tok={token} />}
        {tab === "communications" && <CommunicationsTab tok={token} />}
        {tab === "logs"           && <LogsTab tok={token} />}
      </div>
    </div>
  );
}
